import { Heading, IconButton, Image } from '@alto-avios/alto-ui';
import { Dialog, Modal as AriaModal, ModalOverlay } from 'react-aria-components';
import { clsx } from 'clsx';
import { useRef } from 'react';

import { pushtoGTM } from '../../utilities';
import { Button } from '../Button';
import { ModalData } from '../services/content';
import { Markdown } from '../Markdown';

import styles from './Modal.module.css';

import '@alto-avios/alto-tokens/web/avios/fonts.css';

export interface ModalProps {
  data: ModalData;
  onClose: () => void;
}

export const ModalComponent = ({
  data: {
    ariaLabel,
    closeButtonAriaLabel,
    heading,
    image,
    paragraph,
    primaryButtonHref,
    primaryButtonText,
    secondaryButtonHref,
    secondaryButtonText,
    size,
  },
  onClose,
}: ModalProps) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  return (
    <ModalOverlay
      className={styles.overlay}
      isOpen
      isDismissable
      onOpenChange={(isOpen) => !isOpen && onClose()}
      shouldCloseOnInteractOutside={(element) => overlayRef.current === element}
      ref={overlayRef}
    >
      <AriaModal className={clsx(styles.modal, styles[size])}>
        <Dialog aria-label={ariaLabel} className={styles.dialog}>
          <div className={styles.dismiss}>
            <IconButton
              aria-label={closeButtonAriaLabel}
              emphasis="quaternary"
              iconProps={{ iconName: 'times' }}
              onClick={() => {
                pushtoGTM('ctaEvent', `modal - ${heading}`, 'cta', 'close modal');
                onClose();
              }}
              styleVariant="neutral"
            />
          </div>
          <div className={styles.image}>
            <Image alt={image.description} aria-label={image.title} src={image.url} fit="contain" />
          </div>
          <Heading foregroundColor="default" size="md" textAlign="center">
            {heading}
          </Heading>
          <div className={styles.body}>
            <Markdown>{paragraph}</Markdown>
          </div>
          <div className={styles.footer}>
            {secondaryButtonText && (
              <Button
                fullWidth
                href={secondaryButtonHref}
                emphasis="secondary"
                onPress={() => {
                  pushtoGTM('ctaEvent', `modal - ${heading}`, 'cta', secondaryButtonText);

                  setTimeout(onClose, 0);
                }}
              >
                {secondaryButtonText}
              </Button>
            )}
            <Button
              fullWidth={!!secondaryButtonHref || size === 'small'}
              href={primaryButtonHref}
              onPress={() => {
                pushtoGTM('ctaEvent', `modal - ${heading}`, 'cta', primaryButtonText);

                setTimeout(onClose, 0);
              }}
            >
              {primaryButtonText}
            </Button>
          </div>
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
};
